import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  User,
  LogOut,
  HelpCircle,
  Sun,
  Moon,
  Menu,
  X,
  Keyboard,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useSignOut } from '../hooks/auth';
import { useAuth } from '../hooks/auth';

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  collapsed?: boolean;
  onClick?: () => void;
}

function NavItem({ to, icon, label, collapsed = false, onClick }: NavItemProps) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
          isActive
            ? 'bg-[#1DA868]/10 text-[#1DA868]'
            : 'text-text-secondary hover:bg-elevated hover:text-text-primary'
        } ${collapsed ? 'justify-center px-2' : ''}`
      }
      title={collapsed ? label : undefined}
    >
      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">{icon}</span>
      {!collapsed && <span className="font-medium">{label}</span>}
    </NavLink>
  );
}

interface SidebarProps {
  currentTheme?: 'light' | 'dark';
  onToggleTheme?: () => void;
  // Controlled collapsed state
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  // Callback for keyboard shortcuts
  onShowShortcutsHelp?: () => void;
}



export function Sidebar({
  currentTheme = 'dark',
  onToggleTheme,
  collapsed: controlledCollapsed,
  onCollapsedChange,
  onShowShortcutsHelp,
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const signOut = useSignOut();
  const { user } = useAuth();

  // Internal collapsed state (if not controlled) - always collapsed by default
  const [internalCollapsed, setInternalCollapsed] = useState(true);

  // Use controlled state if provided, otherwise use internal
  const collapsed = controlledCollapsed ?? internalCollapsed;
  const setCollapsed = (value: boolean) => {
    if (onCollapsedChange) {
      onCollapsedChange(value);
    } else {
      setInternalCollapsed(value);
    }
  };

  // Mobile menu state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(collapsed));
  }, [collapsed]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Logout confirmation modal state
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutConfirm(false);
    await signOut.mutateAsync();
    navigate('/login');
  };

  // Fixed widths
  const sidebarWidthStyle = collapsed ? '64px' : '240px';

  return (
    <>
      {/* Mobile Hamburger Button - hidden when menu is open */}
      {!mobileOpen && (
        <button
          onClick={() => setMobileOpen(true)}
          className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-surface border border-border rounded-lg shadow-lg"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
      )}

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`h-screen bg-surface border-r border-border flex flex-col transition-all duration-300 ease-in-out
          fixed left-0 top-0 z-40
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{ width: sidebarWidthStyle, minWidth: sidebarWidthStyle, maxWidth: sidebarWidthStyle }}
      >
        {/* Logo */}
        <div className="h-14 border-b border-border flex items-center px-4">
          <div className={`flex items-center gap-2 w-full ${collapsed ? 'justify-center' : ''}`}>
            <div className="w-8 h-8 rounded-lg hidden lg:flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src="/logo.webp" alt="Logo" className="w-full h-full object-contain" />
            </div>
            {!collapsed && (
              <span className="font-semibold text-[#1DA868] truncate">
                EventMap
              </span>
            )}
            {/* Mobile Close Button */}
            <button
              onClick={() => setMobileOpen(false)}
              className="lg:hidden ml-auto p-2 text-text-secondary hover:text-text-primary hover:bg-elevated rounded-lg transition-colors flex-shrink-0"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <NavItem
            to="/dashboard"
            icon={<LayoutDashboard size={20} />}
            label="Dashboard"
            collapsed={collapsed}
          />
          <NavItem
            to="/profile"
            icon={<User size={20} />}
            label="Профіль"
            collapsed={collapsed}
          />
          <NavItem
            to="/help"
            icon={<HelpCircle size={20} />}
            label="Довідка"
            collapsed={collapsed}
          />

          {/* Keyboard Shortcuts Menu Item */}
          {onShowShortcutsHelp && (
            <button
              onClick={() => {
                onShowShortcutsHelp();
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-text-secondary hover:bg-elevated hover:text-text-primary ${
                collapsed ? 'justify-center px-2' : ''
              }`}
              title={collapsed ? 'Гарячі клавіші' : undefined}
            >
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <Keyboard size={20} />
              </span>
              {!collapsed && <span className="font-medium">Гарячі клавіші</span>}
            </button>
          )}
        </nav>

        {/* Bottom Actions */}
        <div className="p-3 border-t border-border space-y-2">
          {/* Theme Toggle */}
          <button
            onClick={onToggleTheme}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-text-secondary hover:bg-elevated hover:text-text-primary ${
              collapsed ? 'justify-center px-2' : ''
            }`}
            title={collapsed ? (currentTheme === 'dark' ? 'Світла тема' : 'Темна тема') : undefined}
          >
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              {currentTheme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </span>
            {!collapsed && <span className="font-medium">Тема</span>}
          </button>

          {/* Collapse Toggle (Desktop only) */}
          <button
            onClick={() => {
              setCollapsed(!collapsed);
            }}
            className={`hidden lg:flex w-full items-center gap-3 px-4 py-3 rounded-lg transition-colors text-text-secondary hover:bg-elevated hover:text-text-primary ${
              collapsed ? 'justify-center px-2' : ''
            }`}
            title={collapsed ? 'Розгорнути' : 'Згорнути'}
          >
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </span>
            {!collapsed && <span className="font-medium">Згорнути</span>}
          </button>

          {/* Logout */}
          <button
            onClick={handleLogoutClick}
            disabled={signOut.isPending}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-red-400 hover:bg-red-500/10 ${
              collapsed ? 'justify-center px-2' : ''
            }`}
            title={collapsed ? 'Вийти' : undefined}
          >
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              {signOut.isPending ? (
                <div className="w-5 h-5 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
              ) : (
                <LogOut size={20} />
              )}
            </span>
            {!collapsed && <span className="font-medium">Вийти</span>}
          </button>
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowLogoutConfirm(false)}
          />
          {/* Modal */}
          <div className="relative z-10 w-full max-w-sm mx-4 bg-surface border border-border rounded-xl shadow-2xl p-6">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <LogOut size={24} className="text-red-400" />
              </div>
            </div>

            {/* Title */}
            <h3 className="text-lg font-semibold text-text-primary text-center mb-2">
              Підтвердження виходу
            </h3>

            {/* Description */}
            <p className="text-text-secondary text-center text-sm mb-6">
              Ви впевнені, що хочете вийти з системи? Всі незбережені зміни будуть втрачені.
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-border text-text-secondary hover:bg-elevated transition-colors font-medium"
              >
                Скасувати
              </button>
              <button
                onClick={handleConfirmLogout}
                disabled={signOut.isPending}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {signOut.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Вихід...
                  </>
                ) : (
                  'Вийти'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sidebar;
